import api from "./api";
import { getAdminProducts } from "./productsApi";
import { getBusinessServices, getCreateFormData } from "./businessServicesApi";
import { Product } from "../types/product";
import { BusinessService } from "../types/businessService";
import { PosTax } from "../lib/posCart";
import {
  catalogCacheKey,
  invalidateCatalogCache,
  isCatalogCacheFresh,
  readCatalogCache,
  writeCatalogCache,
} from "../lib/catalogCache";
import { isBrowserOffline, shouldUseCachedData } from "../lib/networkStatus";

export interface PosCatalogData {
  products: Product[];
  services: BusinessService[];
  taxes: PosTax[];
  customers: unknown[];
}

export interface CatalogLoadOptions<TCached = void> {
  force?: boolean;
  onCached?: (cached: TCached) => void;
}

const WALKIN_CUSTOMER = {
  id: 4,
  name: "Walk-in Customer",
  email: "",
  mobile: "",
};

async function fetchPosCustomers(): Promise<unknown[]> {
  const response = await api.get(`/admin/customers?per_page=1000`);
  const payload = response?.data?.data;
  let apiCustomers: unknown[] = [];

  if (Array.isArray(payload?.data)) {
    apiCustomers = payload.data;
  } else if (Array.isArray(payload)) {
    apiCustomers = payload;
  }

  return [WALKIN_CUSTOMER, ...apiCustomers];
}

export async function loadProductsCatalog(
  userId: string,
  options?: CatalogLoadOptions<Product[]>,
): Promise<Product[]> {
  const key = catalogCacheKey("products", userId);
  const cached = readCatalogCache<Product[]>(key);

  if (cached?.data?.length) {
    options?.onCached?.(cached.data);
  }

  const shouldFetch =
    !isBrowserOffline() &&
    (options?.force || !cached || !isCatalogCacheFresh(cached.fetchedAt));

  if (!shouldFetch) {
    return cached?.data ?? [];
  }

  try {
    const fresh = await getAdminProducts();
    if (fresh.length > 0) {
      writeCatalogCache(key, fresh);
      return fresh;
    }
    return cached?.data ?? [];
  } catch (error) {
    if (shouldUseCachedData(error)) {
      console.warn("Using cached products (offline or network error)");
    } else {
      console.error("Failed to fetch products catalog", error);
    }
    return cached?.data ?? [];
  }
}

export async function loadServicesCatalog(
  userId: string,
  options?: CatalogLoadOptions<BusinessService[]>,
): Promise<{ services: BusinessService[]; taxes: PosTax[] }> {
  const servicesKey = catalogCacheKey("services", userId);
  const taxesKey = catalogCacheKey("taxes", userId);

  const cachedServices = readCatalogCache<BusinessService[]>(servicesKey);
  const cachedTaxes = readCatalogCache<PosTax[]>(taxesKey);

  if (cachedServices?.data?.length) {
    options?.onCached?.(cachedServices.data);
  }

  const shouldFetch =
    !isBrowserOffline() &&
    (options?.force ||
      !cachedServices ||
      !isCatalogCacheFresh(cachedServices.fetchedAt));

  if (!shouldFetch) {
    return {
      services: cachedServices?.data ?? [],
      taxes: cachedTaxes?.data ?? [],
    };
  }

  try {
    const [services, formData] = await Promise.all([
      getBusinessServices(),
      getCreateFormData(),
    ]);

    const activeServices = services.filter((s) => s.status === "active");
    const taxes: PosTax[] = (formData.taxes || []).map((tax) => ({
      id: tax.id,
      percent: tax.percent,
    }));

    if (activeServices.length > 0) {
      writeCatalogCache(servicesKey, activeServices);
    }
    if (taxes.length > 0) {
      writeCatalogCache(taxesKey, taxes);
    }

    return { services: activeServices, taxes };
  } catch (error) {
    if (shouldUseCachedData(error)) {
      console.warn("Using cached services (offline or network error)");
    } else {
      console.error("Failed to fetch services catalog", error);
    }
    return {
      services: cachedServices?.data ?? [],
      taxes: cachedTaxes?.data ?? [],
    };
  }
}

export async function loadPosCatalog(
  userId: string,
  options?: CatalogLoadOptions<PosCatalogData>,
): Promise<PosCatalogData> {
  const key = catalogCacheKey("pos", userId);
  const cached = readCatalogCache<PosCatalogData>(key);

  if (cached?.data) {
    options?.onCached?.(cached.data);
  }

  const shouldFetch =
    !isBrowserOffline() &&
    (options?.force || !cached || !isCatalogCacheFresh(cached.fetchedAt));

  if (!shouldFetch && cached?.data) {
    return cached.data;
  }

  try {
    const [products, serviceBundle, customers] = await Promise.all([
      loadProductsCatalog(userId, { force: options?.force }),
      loadServicesCatalog(userId, { force: options?.force }),
      fetchPosCustomers(),
    ]);

    const data: PosCatalogData = {
      products,
      services: serviceBundle.services,
      taxes: serviceBundle.taxes,
      customers,
    };

    writeCatalogCache(key, data);
    return data;
  } catch (error) {
    if (shouldUseCachedData(error) && cached?.data) {
      console.warn("Using cached POS catalog (offline or network error)");
      return cached.data;
    }
    console.error("Failed to fetch POS catalog", error);
    return (
      cached?.data ?? {
        products: [],
        services: [],
        taxes: [],
        customers: [WALKIN_CUSTOMER],
      }
    );
  }
}

export function invalidateProductsCatalog(userId: string): void {
  invalidateCatalogCache(catalogCacheKey("products", userId));
  invalidateCatalogCache(catalogCacheKey("pos", userId));
}

export function invalidateServicesCatalog(userId: string): void {
  invalidateCatalogCache(catalogCacheKey("services", userId));
  invalidateCatalogCache(catalogCacheKey("taxes", userId));
  invalidateCatalogCache(catalogCacheKey("pos", userId));
}

export function invalidatePosCatalog(userId: string): void {
  invalidateCatalogCache(catalogCacheKey("pos", userId));
}
